import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'

import { useAuth, useCartRedux, useDashboardRedux, useUserRedux } from '@hooks'
import { DiningMethodSelector, PickupDateTimePicker } from '@components'
import { convertToSingaporeDateTime } from '@utils'
import { COLORS, DINING_METHOD, ROUTES, customToast } from '@common'

const DiningMethodEditModal = ({ isOpen, toggle, currentDiningMethod, currentTableId, hasItems }) => {
    const navigate = useNavigate()
    const { setDiningMethod, setTableId, setPickupDate, setPickupTime, pickupDate, pickupTime } = useUserRedux()
    const { tables } = useDashboardRedux()
    const { clearCart } = useCartRedux()
    const { isAuthenticated } = useAuth()

    const [newDiningMethod, setNewDiningMethod] = useState(currentDiningMethod)
    const [newTableId, setNewTableId] = useState(currentTableId || '')
    const [pickupDateTime, setPickupDateTime] = useState(null)
    const [showWarning, setShowWarning] = useState(false)

    // Initialize pickupDateTime when modal opens
    useEffect(() => {
        if (pickupDate && pickupTime) {
            const dateTime = convertToSingaporeDateTime(pickupDate, pickupTime)
            setPickupDateTime(dateTime)
        } else {
            setPickupDateTime(null)
        }
    }, [pickupDate, pickupTime, isOpen])

    const onDiningMethodChange = value => {
        // Check if user selected Advance Takeaway but not logged in
        if (value.value === DINING_METHOD.AdvanceTakeaway && !isAuthenticated) {
            customToast.plain('Sorry, you must be logged in to select Advance Takeaway', {
                icon: null,
                duration: 3000,
                closeButton: close => (
                    <button
                        onClick={() => {
                            navigate(ROUTES.Membership.auth)
                            close()
                        }}
                        style={{
                            padding: '6px 12px',
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#fff',
                            backgroundColor: COLORS.PRIMARY,
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Login
                    </button>
                ),
            })
            return
        }

        setNewDiningMethod(value)

        // Auto set Unknown table when DineIn is selected
        if (value.value === DINING_METHOD.DineIn) {
            const unknownTable = tables.find(table => table.name === 'Unknown')
            if (unknownTable) {
                setNewTableId({ value: unknownTable.id, label: 'Unknown' })
            } else {
                setNewTableId(null)
            }
        } else {
            setNewTableId(null)
        }

        // Clear pickup date/time when switching away from Advance Takeaway
        if (value.value !== DINING_METHOD.AdvanceTakeaway) {
            setPickupDateTime(null)
        }
    }

    const handleDateTimeChange = ({ date, time }) => {
        const dateTime = convertToSingaporeDateTime(date, time)
        setPickupDateTime(dateTime)
    }

    const handleSaveChanges = () => {
        if (newDiningMethod.value !== currentDiningMethod.value && hasItems) {
            setShowWarning(true)
        } else {
            handleConfirmChange()
        }
    }

    const handleConfirmChange = () => {
        if (newDiningMethod.value !== currentDiningMethod.value) {
            setDiningMethod(newDiningMethod)
            clearCart()
        }

        if (newDiningMethod.value === DINING_METHOD.DineIn) {
            if (!!newTableId?.value && newTableId?.value !== currentTableId?.value) {
                setTableId(newTableId)
            } else {
                setTableId(null)
            }
            setPickupDate(null)
            setPickupTime(null)
        } else if (newDiningMethod.value === DINING_METHOD.AdvanceTakeaway) {
            setTableId(null)
            if (pickupDateTime?.date) {
                setPickupDate(pickupDateTime.date)
            }
            if (pickupDateTime?.time) {
                setPickupTime(pickupDateTime.time)
            }
        } else {
            setTableId(null)
            setPickupDate(null)
            setPickupTime(null)
        }
        setShowWarning(false)
        toggle()
    }

    const handleCancel = () => {
        setShowWarning(false)
        setNewDiningMethod(currentDiningMethod)
        setNewTableId(currentTableId)
        setPickupDateTime(pickupDate && pickupTime ? { date: pickupDate, time: pickupTime } : null)
        toggle()
    }

    return (
        <>
            <Modal isOpen={isOpen} toggle={handleCancel}>
                <ModalHeader toggle={handleCancel}>Edit Dining Method</ModalHeader>
                <ModalBody>
                    <DiningMethodSelector
                        mode="Dropdown"
                        value={newDiningMethod}
                        onChange={onDiningMethodChange}
                        containerClassName="mt-2"
                    />
                    {newDiningMethod.value === DINING_METHOD.AdvanceTakeaway && (
                        <PickupDateTimePicker value={pickupDateTime} onChange={handleDateTimeChange} containerClassName="mt-3" />
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onClick={handleSaveChanges}
                        disabled={
                            (newDiningMethod.value === currentDiningMethod.value &&
                                newTableId?.value === currentTableId?.value &&
                                !pickupDateTime &&
                                !(pickupDate && pickupTime)) ||
                            (newDiningMethod.value === DINING_METHOD.AdvanceTakeaway && !pickupDateTime)
                        }
                    >
                        Save Changes
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={showWarning} toggle={() => setShowWarning(false)}>
                <ModalHeader toggle={() => setShowWarning(false)}>Warning</ModalHeader>
                <ModalBody>
                    Changing dining method will clear your cart. All items will need to be re-selected. Do you want to continue?
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setShowWarning(false)}>
                        Cancel
                    </Button>
                    <Button color="danger" onClick={handleConfirmChange}>
                        Continue
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    )
}

export default DiningMethodEditModal
